import { 
  collection, 
  doc, 
  getDoc, 
  getDocs,
  setDoc, 
  updateDoc, 
  onSnapshot,
  query,
  where
} from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

/**
 * Get user document reference
 * @param {string} userId - User ID
 * @returns {DocumentReference} Firestore document reference
 */
const getUserDocRef = (userId) => {
  return doc(db, 'users', userId);
};

/**
 * Fetch user data from Firestore
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} User data or null if not found
 */
export const fetchUserData = async (userId) => {
  try {
    const userDocRef = getUserDocRef(userId);
    const userDocSnap = await getDoc(userDocRef);
    
    if (userDocSnap.exists()) {
      return userDocSnap.data();
    } else {
      // Return default user structure if document doesn't exist
      return {
        username: 'Anonymous User',
        bio: '',
        socialLinks: {
          twitch: '',
          customLink: { name: '', link: '' }
        },
        xpHistory: {}
      };
    }
  } catch (error) {
    console.error('Error fetching user data:', error);
    throw error;
  }
};

/**
 * Create or update user document
 * @param {string} userId - User ID
 * @param {Object} userData - User data to save
 * @returns {Promise<void>}
 */
export const saveUserData = async (userId, userData) => {
  try {
    const userDocRef = getUserDocRef(userId);
    await setDoc(userDocRef, userData, { merge: true });
  } catch (error) {
    console.error('Error saving user data:', error);
    throw error;
  }
};

/**
 * Update user's XP history with a new quest entry
 * @param {string} userId - User ID
 * @param {string} questId - Quest ID
 * @param {Object} questData - Quest data (status, type, value, etc.)
 * @returns {Promise<void>}
 */
export const updateQuestInHistory = async (userId, questId, questData) => {
  try {
    const userDocRef = getUserDocRef(userId);
    const updateData = {
      [`xpHistory.${questId}`]: {
        ...questData,
        updatedAt: Date.now()
      }
    };
    
    await updateDoc(userDocRef, updateData);
  } catch (error) {
    console.error('Error updating quest in history:', error);
    throw error;
  }
};

/**
 * Add a new quest to user's XP history
 * @param {string} userId - User ID
 * @param {Object} questData - Quest data
 * @returns {Promise<string>} Generated quest ID
 */
export const addNewQuest = async (userId, questData) => {
  try {
    // Generate a unique quest ID
    const questId = `-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    await updateQuestInHistory(userId, questId, {
      ...questData,
      createdAt: Date.now(),
      status: questData.status || 'active'
    });
    
    return questId;
  } catch (error) {
    console.error('Error adding new quest:', error);
    throw error;
  }
};

/**
 * Complete a quest and update XP history
 * @param {string} userId - User ID
 * @param {string} questId - Quest ID
 * @param {number} xpValue - XP value to award
 * @returns {Promise<void>}
 */
export const completeQuest = async (userId, questId, xpValue) => {
  try {
    await updateQuestInHistory(userId, questId, {
      status: 'completed',
      value: xpValue,
      completedAt: Date.now(),
      type: 'quest'
    });
  } catch (error) {
    console.error('Error completing quest:', error);
    throw error;
  }
};

/**
 * Fail a quest and update XP history
 * @param {string} userId - User ID
 * @param {string} questId - Quest ID
 * @param {number} xpValue - Original XP value (for reference)
 * @returns {Promise<void>}
 */
export const failQuest = async (userId, questId, xpValue) => {
  try {
    await updateQuestInHistory(userId, questId, {
      status: 'failed',
      value: xpValue,
      failedAt: Date.now(),
      type: 'quest'
    });
  } catch (error) {
    console.error('Error failing quest:', error);
    throw error;
  }
};

/**
 * Abandon an active quest
 * @param {string} userId - User ID
 * @param {string} questId - Quest ID
 * @returns {Promise<void>}
 */
export const abandonQuest = async (userId, questId) => {
  try {
    const userData = await fetchUserData(userId);
    const quest = userData.xpHistory[questId];
    
    if (quest && quest.status === 'active') {
      await failQuest(userId, questId, quest.value || 0);
    }
  } catch (error) {
    console.error('Error abandoning quest:', error);
    throw error;
  }
};

/**
 * Complete the active quest and clear activeQuest field
 * @param {string} userId - User ID
 * @param {number} xpValue - XP value to award
 * @returns {Promise<void>}
 */
export const completeActiveQuest = async (userId, xpValue) => {
  try {
    const userDocRef = getUserDocRef(userId);
    
    // Clear the activeQuest field and update user data
    await updateDoc(userDocRef, {
      activeQuest: null,
      // XP history updates will be handled separately later
      updatedAt: Date.now()
    });
  } catch (error) {
    console.error('Error completing active quest:', error);
    throw error;
  }
};

/**
 * Generate download URL for a file stored in Firebase Storage
 * @param {string} storagePath - Path to file in Firebase Storage
 * @returns {Promise<string>} Download URL
 */
export const generateDownloadURL = async (storagePath) => {
  try {
    const storageRef = ref(storage, storagePath);
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error('Error generating download URL:', error);
    throw error;
  }
};

/**
 * Submit quest completion with proof for admin review
 * @param {string} userId - User ID
 * @param {string} questId - Quest ID
 * @param {Object} questData - Quest data
 * @param {Array} mainQuestProofs - Main quest proof submissions from user
 * @param {Object} sideQuestProofs - Side quest proof submissions from user
 * @returns {Promise<string>} Completion request ID
 */
export const submitQuestCompletion = async (userId, questId, questData, mainQuestProofs, sideQuestProofs = {}) => {
  try {
    const completionId = `completion_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Create a quest completion document in a separate collection
    const completionDocRef = doc(db, 'questCompletions', completionId);
    
    // Upload files to Storage if provided and build normalized proofs
    const normalizedProofs = [];
    for (const p of (mainQuestProofs || [])) {
      const inputType = (p.inputType || 'link').toLowerCase();
      const requiresUpload = (inputType === 'photo' || inputType === 'image' || inputType === 'pdf' || inputType === 'excel');
      if (requiresUpload && p.file instanceof File) {
        const file = p.file;
        // Derive a stable name from the label so re-uploads replace the previous file
        const extractLabelText = (label) => {
          if (!label) return 'proof';
          if (typeof label === 'string') return label;
          if (typeof label === 'object') {
            return label.en || label.tr || label.nl || Object.values(label)[0] || 'proof';
          }
          return 'proof';
        };
        const slugify = (text) => String(text)
          .normalize('NFKD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '') || 'proof';
        const labelText = extractLabelText(p.label);
        const labelSlug = slugify(labelText);
        const inferExt = () => {
          const fromName = (file.name || '').split('.').pop();
          if (fromName && /^[a-zA-Z0-9]{1,10}$/.test(fromName)) return fromName.toLowerCase();
          const type = (file.type || '').toLowerCase();
          if (type === 'application/pdf') return 'pdf';
          if (type === 'image/jpeg') return 'jpg';
          if (type === 'image/png') return 'png';
          if (type === 'image/gif') return 'gif';
          if (type.includes('csv')) return 'csv';
          if (type.includes('spreadsheet') || type.includes('excel')) return 'xlsx';
          return 'bin';
        };
        const ext = inferExt();
        const storagePath = `questProofs/${userId}/${questId}/${labelSlug}.${ext}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file, { contentType: file.type || undefined });
        normalizedProofs.push({
          label: p.label,
          inputType,
          value: file.name, // Store filename instead of download URL
          storagePath, // Keep storage path for on-demand download generation
          fileSize: file.size,
          fileType: file.type
        });
      } else {
        normalizedProofs.push({
          label: p.label,
          inputType,
          value: p.value || ''
        });
      }
    }

    // Process side quest proofs
    const normalizedSideQuestProofs = {};
    for (const [sideQuestIndex, sideQuestProofsArray] of Object.entries(sideQuestProofs)) {
      const sideQuestNormalizedProofs = [];
      for (const p of (sideQuestProofsArray || [])) {
        const inputType = (p.inputType || 'link').toLowerCase();
        const requiresUpload = (inputType === 'photo' || inputType === 'image' || inputType === 'pdf' || inputType === 'excel');
        if (requiresUpload && p.file instanceof File) {
          const file = p.file;
          // Derive a stable name from the label so re-uploads replace the previous file
          const extractLabelText = (label) => {
            if (!label) return 'proof';
            if (typeof label === 'string') return label;
            if (typeof label === 'object') {
              return label.en || label.tr || label.nl || Object.values(label)[0] || 'proof';
            }
            return 'proof';
          };
          const slugify = (text) => String(text)
            .normalize('NFKD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '') || 'proof';
          const labelText = extractLabelText(p.label);
          const labelSlug = slugify(labelText);
          const inferExt = () => {
            const fromName = (file.name || '').split('.').pop();
            if (fromName && /^[a-zA-Z0-9]{1,10}$/.test(fromName)) return fromName.toLowerCase();
            const type = (file.type || '').toLowerCase();
            if (type === 'application/pdf') return 'pdf';
            if (type === 'image/jpeg') return 'jpg';
            if (type === 'image/png') return 'png';
            if (type === 'image/gif') return 'gif';
            if (type.includes('csv')) return 'csv';
            if (type.includes('spreadsheet') || type.includes('excel')) return 'xlsx';
            return 'bin';
          };
          const ext = inferExt();
          const storagePath = `questProofs/${userId}/${questId}/side-${sideQuestIndex}/${labelSlug}.${ext}`;
          const storageRef = ref(storage, storagePath);
          await uploadBytes(storageRef, file, { contentType: file.type || undefined });

          sideQuestNormalizedProofs.push({
            label: p.label,
            inputType,
            value: file.name, // Store filename instead of download URL
            storagePath, // Keep storage path for on-demand download generation
            fileSize: file.size,
            fileType: file.type
          });
        } else {
          sideQuestNormalizedProofs.push({
            label: p.label,
            inputType,
            value: p.value || ''
          });
        }
      }
      normalizedSideQuestProofs[sideQuestIndex] = sideQuestNormalizedProofs;
    }

    const completionData = {
      id: completionId,
      userId,
      questId,
      questData,
      proofSubmissions: normalizedProofs,
      sideQuestProofSubmissions: normalizedSideQuestProofs,
      status: 'pending', // pending, approved, rejected
      submittedAt: Date.now(),
      reviewedAt: null,
      reviewedBy: null,
      reviewNotes: ''
    };
    
    await setDoc(completionDocRef, completionData);
    
    // Quest remains in activeQuest until admin approves/rejects
    // No need to update xpHistory - only questCompletions tracks the submission
    
    return completionId;
  } catch (error) {
    console.error('Error submitting quest completion:', error);
    throw error;
  }
};

/**
 * Abandon the active quest and clear activeQuest field
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export const abandonActiveQuest = async (userId) => {
  try {
    const userDocRef = getUserDocRef(userId);
    
    // Clear the activeQuest field
    await updateDoc(userDocRef, {
      activeQuest: null,
      updatedAt: Date.now()
    });
  } catch (error) {
    console.error('Error abandoning active quest:', error);
    throw error;
  }
};

/**
 * Subscribe to user data changes
 * @param {string} userId - User ID
 * @param {Function} callback - Callback function to handle data changes
 * @returns {Function} Unsubscribe function
 */
export const subscribeToUserData = (userId, callback) => {
  const userDocRef = getUserDocRef(userId);
  
  return onSnapshot(userDocRef, (docSnap) => {
    if (docSnap.exists()) {
      callback(docSnap.data());
    } else {
      // Return default structure if document doesn't exist
      callback({
        username: 'Anonymous User',
        bio: '',
        socialLinks: {
          twitch: '',
          customLink: { name: '', link: '' }
        },
        xpHistory: {}
      });
    }
  }, (error) => {
    console.error('Error in user data subscription:', error);
    callback(null); // Signal error to callback
  });
};

/**
 * Initialize user document with default data if it doesn't exist
 * @param {string} userId - User ID
 * @param {string} username - Username to set
 * @returns {Promise<void>}
 */
export const initializeUserDocument = async (userId, username = 'Anonymous User') => {
  try {
    const userDocRef = getUserDocRef(userId);
    const userDocSnap = await getDoc(userDocRef);
    
    if (!userDocSnap.exists()) {
      await setDoc(userDocRef, {
        username: username,
        bio: '',
        socialLinks: {
          twitch: '',
          customLink: { name: '', link: '' }
        },
        xpHistory: {},
        createdAt: Date.now(),
        lastActive: Date.now()
      });
    } else {
      // Update last active timestamp
      await updateDoc(userDocRef, {
        lastActive: Date.now()
      });
    }
  } catch (error) {
    console.error('Error initializing user document:', error);
    throw error;
  }
};

/**
 * Update username
 * @param {string} userId - User ID
 * @param {string} newUsername - New username
 * @returns {Promise<void>}
 */
export const updateUsername = async (userId, newUsername) => {
  try {
    const userDocRef = getUserDocRef(userId);
    await updateDoc(userDocRef, {
      username: newUsername,
      updatedAt: Date.now()
    });
  } catch (error) {
    console.error('Error updating username:', error);
    throw error;
  }
};

/**
 * Batch update multiple quests (useful for complex operations)
 * @param {string} userId - User ID
 * @param {Object} questUpdates - Object with questId as key and quest data as value
 * @returns {Promise<void>}
 */
export const batchUpdateQuests = async (userId, questUpdates) => {
  try {
    const userDocRef = getUserDocRef(userId);
    const updateData = {};
    
    Object.entries(questUpdates).forEach(([questId, questData]) => {
      updateData[`xpHistory.${questId}`] = {
        ...questData,
        updatedAt: Date.now()
      };
    });
    
    await updateDoc(userDocRef, updateData);
  } catch (error) {
    console.error('Error batch updating quests:', error);
    throw error;
  }
};

/**
 * Update user profile (bio and social links)
 * @param {string} userId - User ID
 * @param {Object} profileData - Profile data containing bio and social links
 * @returns {Promise<void>}
 */
export const updateUserProfile = async (userId, profileData) => {
  try {
    const userDocRef = getUserDocRef(userId);
    await updateDoc(userDocRef, {
      ...profileData,
      updatedAt: Date.now()
    });
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Update user bio
 * @param {string} userId - User ID
 * @param {string} bio - User bio
 * @returns {Promise<void>}
 */
export const updateUserBio = async (userId, bio) => {
  try {
    const userDocRef = getUserDocRef(userId);
    await updateDoc(userDocRef, {
      bio: bio,
      updatedAt: Date.now()
    });
  } catch (error) {
    console.error('Error updating user bio:', error);
    throw error;
  }
};

/**
 * Update user social links
 * @param {string} userId - User ID
 * @param {Object} socialLinks - Social links object { twitch: '', customLink: { name: '', link: '' } }
 * @returns {Promise<void>}
 */
export const updateUserSocialLinks = async (userId, socialLinks) => {
  try {
    const userDocRef = getUserDocRef(userId);
    await updateDoc(userDocRef, {
      socialLinks: socialLinks,
      updatedAt: Date.now()
    });
  } catch (error) {
    console.error('Error updating social links:', error);
    throw error;
  }
};

/**
 * Update user streaming languages
 * @param {string} userId - User ID
 * @param {Object} streamingLanguages - Languages object { primary: [], secondary: [] }
 * @returns {Promise<void>}
 */
export const updateUserStreamingLanguages = async (userId, streamingLanguages) => {
  try {
    const userDocRef = getUserDocRef(userId);
    await updateDoc(userDocRef, {
      streamingLanguages: streamingLanguages,
      updatedAt: Date.now()
    });
  } catch (error) {
    console.error('Error updating streaming languages:', error);
    throw error;
  }
};

// ===== QUEST REQUESTS FOR DASHBOARD =====

/**
 * Get quest requests for a specific user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of quest request objects for the user
 */
export const getUserQuestRequests = async (userId) => {
  try {
    const requestsCollectionRef = collection(db, 'questRequests');
    const requestsQuery = query(
      requestsCollectionRef, 
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(requestsQuery);
    
    const requests = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by requestedAt in descending order (newest first)
    return requests.sort((a, b) => (b.requestedAt || 0) - (a.requestedAt || 0));
  } catch (error) {
    console.error('Error fetching user quest requests:', error);
    throw error;
  }
};

/**
 * Subscribe to quest requests for a specific user
 * @param {string} userId - User ID
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const subscribeToUserQuestRequests = (userId, callback) => {
  const requestsCollectionRef = collection(db, 'questRequests');
  const requestsQuery = query(
    requestsCollectionRef, 
    where('userId', '==', userId)
  );
  
  return onSnapshot(requestsQuery, (querySnapshot) => {
    const requests = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by requestedAt in descending order (newest first)
    const sortedRequests = requests.sort((a, b) => (b.requestedAt || 0) - (a.requestedAt || 0));
    callback(sortedRequests);
  }, (error) => {
    console.error('Error in user quest requests subscription:', error);
    callback([]);
  });
};

/**
 * Get pending quest requests for a specific user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of pending quest request objects
 */
export const getUserPendingQuestRequests = async (userId) => {
  try {
    const requestsCollectionRef = collection(db, 'questRequests');
    const requestsQuery = query(
      requestsCollectionRef, 
      where('userId', '==', userId),
      where('status', '==', 'pending')
    );
    const querySnapshot = await getDocs(requestsQuery);
    
    const requests = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by requestedAt in descending order (newest first)
    return requests.sort((a, b) => (b.requestedAt || 0) - (a.requestedAt || 0));
  } catch (error) {
    console.error('Error fetching user pending quest requests:', error);
    throw error;
  }
};

/**
 * Get approved quest requests for a specific user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of approved quest request objects
 */
export const getUserApprovedQuestRequests = async (userId) => {
  try {
    const requestsCollectionRef = collection(db, 'questRequests');
    const requestsQuery = query(
      requestsCollectionRef, 
      where('userId', '==', userId),
      where('status', '==', 'approved')
    );
    const querySnapshot = await getDocs(requestsQuery);
    
    const requests = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by requestedAt in descending order (newest first)
    return requests.sort((a, b) => (b.requestedAt || 0) - (a.requestedAt || 0));
  } catch (error) {
    console.error('Error fetching user approved quest requests:', error);
    throw error;
  }
};

/**
 * Get rejected quest requests for a specific user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of rejected quest request objects
 */
export const getUserRejectedQuestRequests = async (userId) => {
  try {
    const requestsCollectionRef = collection(db, 'questRequests');
    const requestsQuery = query(
      requestsCollectionRef, 
      where('userId', '==', userId),
      where('status', '==', 'rejected')
    );
    const querySnapshot = await getDocs(requestsQuery);
    
    const requests = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by requestedAt in descending order (newest first)
    return requests.sort((a, b) => (b.requestedAt || 0) - (a.requestedAt || 0));
  } catch (error) {
    console.error('Error fetching user rejected quest requests:', error);
    throw error;
  }
};

// ===== QUEST COMPLETIONS FOR ADMIN REVIEW =====

/**
 * Get all pending quest completions for admin review
 * @returns {Promise<Array>} Array of pending quest completion objects
 */
export const getPendingQuestCompletions = async () => {
  try {
    const completionsCollectionRef = collection(db, 'questCompletions');
    const completionsQuery = query(
      completionsCollectionRef,
      where('status', '==', 'pending')
    );
    const querySnapshot = await getDocs(completionsQuery);
    
    const completions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Sort locally to avoid composite index requirement
    return completions.sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0));
  } catch (error) {
    console.error('Error fetching pending quest completions:', error);
    throw error;
  }
};

/**
 * Subscribe to pending quest completions for admin review
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
/**
 * Subscribe to pending quest completions count only (no doc deserialization).
 * @param {Function} callback - Receives the count (number)
 * @returns {Function} Unsubscribe function
 */
export const subscribeToPendingQuestCompletionsCount = (callback) => {
  const completionsQuery = query(
    collection(db, 'questCompletions'),
    where('status', '==', 'pending')
  );

  return onSnapshot(completionsQuery, (snapshot) => {
    callback(snapshot.size);
  }, (error) => {
    console.error('Error in pending quest completions count subscription:', error);
    callback(0);
  });
};

export const subscribeToPendingQuestCompletions = (callback) => {
  const completionsCollectionRef = collection(db, 'questCompletions');
  const completionsQuery = query(
    completionsCollectionRef,
    where('status', '==', 'pending')
  );
  
  return onSnapshot(completionsQuery, (querySnapshot) => {
    const completions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Group completions by userId and questId
    const groupedCompletions = {};
    
    completions.forEach(completion => {
      const groupKey = `${completion.userId}_${completion.questId}`;
      
      if (!groupedCompletions[groupKey]) {
        groupedCompletions[groupKey] = {
          // Use the latest submission as the main completion
          ...completion,
          revisions: [],
          revisionCount: 0
        };
      }
      
      // Add this completion as a revision
      groupedCompletions[groupKey].revisions.push(completion);
      groupedCompletions[groupKey].revisionCount++;
      
      // Update main completion if this is newer
      if (completion.submittedAt > groupedCompletions[groupKey].submittedAt) {
        Object.assign(groupedCompletions[groupKey], completion);
      }
    });
    
    // Convert to array and sort by latest submission date
    const groupedArray = Object.values(groupedCompletions);
    
    // Sort revisions within each group (newest first) and sort groups by latest submission date
    groupedArray.forEach(group => {
      group.revisions.sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0));
    });
    
    const sorted = groupedArray.sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0));
    
    callback(sorted);
  }, (error) => {
    console.error('Error in pending quest completions subscription:', error);
    callback([]);
  });
};

export const subscribeToAllQuestCompletions = (callback) => {
  const completionsCollectionRef = collection(db, 'questCompletions');

  return onSnapshot(completionsCollectionRef, (querySnapshot) => {
    const completions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Group completions by userId and questId
    const groupedCompletions = {};

    completions.forEach(completion => {
      const groupKey = `${completion.userId}_${completion.questId}`;

      if (!groupedCompletions[groupKey]) {
        groupedCompletions[groupKey] = {
          ...completion,
          revisions: [],
          revisionCount: 0
        };
      }

      groupedCompletions[groupKey].revisions.push(completion);
      groupedCompletions[groupKey].revisionCount++;

      if (completion.submittedAt > groupedCompletions[groupKey].submittedAt) {
        Object.assign(groupedCompletions[groupKey], completion);
      }
    });

    const groupedArray = Object.values(groupedCompletions);

    groupedArray.forEach(group => {
      group.revisions.sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0));
    });

    const sorted = groupedArray.sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0));

    callback(sorted);
  }, (error) => {
    console.error('Error in all quest completions subscription:', error);
    callback([]);
  });
};

/**
 * Approve a quest completion
 * @param {string} completionId - Completion ID
 * @param {string} adminId - Admin user ID
 * @param {string} reviewNotes - Optional review notes
 * @returns {Promise<void>}
 */
export const approveQuestCompletion = async (completionId, adminId, reviewNotes = '') => {
  try {
    const completionDocRef = doc(db, 'questCompletions', completionId);
    const completionDoc = await getDoc(completionDocRef);
    
    if (!completionDoc.exists()) {
      throw new Error('Quest completion not found');
    }
    
    const completionData = completionDoc.data();
    
    // Update the completion status
    await updateDoc(completionDocRef, {
      status: 'approved',
      reviewedAt: Date.now(),
      reviewedBy: adminId,
      reviewNotes
    });
    
    // Complete the quest in user's history and award XP
    await completeQuest(completionData.userId, completionData.questId, completionData.questData.value || 0);
    
    // Clear the user's active quest
    await completeActiveQuest(completionData.userId, completionData.questData.value || 0);
    
  } catch (error) {
    console.error('Error approving quest completion:', error);
    throw error;
  }
};

/**
 * Approve a quest completion with individual quest/side quest approvals
 * @param {string} completionId - Completion ID
 * @param {string} adminId - Admin user ID
 * @param {Object} individualApprovals - Object containing individual approvals
 * @param {number} totalXp - Total XP to award based on approved items
 * @param {string} approvedItems - Description of approved items
 * @returns {Promise<void>}
 */
export const approveQuestCompletionWithIndividualApprovals = async (completionId, adminId, individualApprovals, totalXp, approvedItems) => {
  try {
    const completionDocRef = doc(db, 'questCompletions', completionId);
    const completionDoc = await getDoc(completionDocRef);
    
    if (!completionDoc.exists()) {
      throw new Error('Quest completion not found');
    }
    
    const completionData = completionDoc.data();
    
    // Update the completion status with individual approvals
    await updateDoc(completionDocRef, {
      status: 'approved',
      reviewedAt: Date.now(),
      reviewedBy: adminId,
      reviewNotes: `Approved items: ${approvedItems}`,
      individualApprovals,
      totalXpAwarded: totalXp
    });
    
    // Complete the quest in user's history and award XP (only for approved items)
    await completeQuest(completionData.userId, completionData.questId, totalXp);
    
    // Clear the user's active quest
    await completeActiveQuest(completionData.userId, totalXp);
    
  } catch (error) {
    console.error('Error approving quest completion with individual approvals:', error);
    throw error;
  }
};

/**
 * Reject a quest completion
 * @param {string} completionId - Completion ID
 * @param {string} adminId - Admin user ID
 * @param {string} reviewNotes - Review notes explaining rejection
 * @returns {Promise<void>}
 */
export const rejectQuestCompletion = async (completionId, adminId, reviewNotes = '') => {
  try {
    const completionDocRef = doc(db, 'questCompletions', completionId);
    const completionDoc = await getDoc(completionDocRef);
    
    if (!completionDoc.exists()) {
      throw new Error('Quest completion not found');
    }
    
    const completionData = completionDoc.data();
    
    // Update the completion status
    await updateDoc(completionDocRef, {
      status: 'rejected',
      reviewedAt: Date.now(),
      reviewedBy: adminId,
      reviewNotes
    });
    
    // Reset the quest status back to 'active' so user can try again
    await updateQuestInHistory(completionData.userId, completionData.questId, {
      status: 'active',
      rejectedAt: Date.now(),
      rejectionNotes: reviewNotes
    });
    
  } catch (error) {
    console.error('Error rejecting quest completion:', error);
    throw error;
  }
}; 

/**
 * Subscribe to user's pending quest completion for a specific quest
 * @param {string} userId - User ID
 * @param {string} questId - Quest ID to monitor
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const subscribeToUserPendingQuestCompletion = (userId, questId, callback) => {
  const completionsCollectionRef = collection(db, 'questCompletions');
  const completionsQuery = query(
    completionsCollectionRef,
    where('userId', '==', userId),
    where('questId', '==', questId),
    where('status', '==', 'pending')
  );
  
  return onSnapshot(completionsQuery, (querySnapshot) => {
    if (querySnapshot.empty) {
      callback(null);
    } else {
      // Get all pending completions for this quest
      const completions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by submission date (newest first)
      completions.sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0));
      
      // Return the latest completion with revision count
      const latestCompletion = completions[0];
      callback({
        ...latestCompletion,
        revisionCount: completions.length,
        totalSubmissions: completions.length
      });
    }
  }, (error) => {
    console.error('Error in user pending quest completion subscription:', error);
    callback(null);
  });
};

// ===== XP HISTORY MANAGEMENT FOR ADMIN =====

/**
 * Add a new XP entry to user's XP history
 * @param {string} userId - User ID
 * @param {Object} xpEntryData - XP entry data (title, value, type, status, etc.)
 * @returns {Promise<string>} Generated entry ID
 */
export const addXPEntryToUser = async (userId, xpEntryData) => {
  try {
    // Generate a unique entry ID
    const entryId = `admin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const userDocRef = getUserDocRef(userId);
    const updateData = {
      [`xpHistory.${entryId}`]: {
        ...xpEntryData,
        updatedAt: Date.now(),
        createdAt: xpEntryData.createdAt || Date.now()
      },
      updatedAt: Date.now()
    };
    
    await updateDoc(userDocRef, updateData);
    return entryId;
  } catch (error) {
    console.error('Error adding XP entry to user:', error);
    throw error;
  }
};

/**
 * Delete an XP entry from user's XP history
 * @param {string} userId - User ID
 * @param {string} entryId - XP entry ID to delete
 * @returns {Promise<void>}
 */
export const deleteXPEntryFromUser = async (userId, entryId) => {
  try {
    const userDocRef = getUserDocRef(userId);
    
    // Get current user data to access xpHistory
    const userDoc = await getDoc(userDocRef);
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    
    const userData = userDoc.data();
    const xpHistory = userData.xpHistory || {};
    
    // Check if entry exists
    if (!xpHistory[entryId]) {
      throw new Error('XP entry not found');
    }
    
    // Create new xpHistory without the entry to delete
    const updatedXpHistory = { ...xpHistory };
    delete updatedXpHistory[entryId];
    
    // Update the user document
    await updateDoc(userDocRef, {
      xpHistory: updatedXpHistory,
      updatedAt: Date.now()
    });
  } catch (error) {
    console.error('Error deleting XP entry from user:', error);
    throw error;
  }
}; 